/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { IconEdit, IconEye } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

import formatDate from '../../../../utilities/helper';
import useDebounce from '../../../../utilities/useDebounce';
import NewCommonModal from '../../../../components/common/NewCommonModal';
import ListView from '../../../../components/common/ListView';
import TableSkeleton from '../../../../components/skeleton/TableSkeleton';
import Pagination from '../../../../components/common/Pagination';
import NormalSearch from '../../../../components/common/NormalSearch';

import { useLocationSchemas } from './locationSchemas';
import { useCityList, useCreateCity, useUpdateCity, useAllCountries } from './locationHooks';
import DynamicForm from './DynamicForm';

export default function CityManageTab({ tab, setTab }: any) {
  const [searchFor, setSearchFor] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchFor, 500);

  const { data: countriesList } = useAllCountries();
  const { data: cityData, isLoading } = useCityList({
    countryId: tab.params,
    page,
    searchFor: debouncedSearch,
  });
  const createMutation = useCreateCity();
  const updateMutation = useUpdateCity();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const { schemas } = useLocationSchemas(countriesList);
  const citySchema = {
    ...schemas.cityAdd,
    fields: schemas.cityAdd?.fields?.map((field: any) => {
      if (isEdit && ['name', 'arabicName', 'cityCode'].includes(field.name)) {
        return { ...field, disabled: true };
      }
      return field;
    }) || [],
  };
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<any>({ mode: 'onTouched' });

  const handleFormSubmit = (data: any) => {
    const payload = { ...data, countryId: tab.params };
    const mutation = isEdit ? updateMutation : createMutation;
    mutation.mutate(isEdit ? { id: selectedItem.id, payload } : payload, {
      onSuccess: () => {
        toast.success(`City ${isEdit ? 'updated' : 'added'} successfully`);
        closeAndReset();
      },
      onError: (err: any) => toast.error(err?.message || 'Operation failed'),
    });
  };

  const closeAndReset = () => {
    reset();
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedItem(null);
  };

  const columns = [
    {
      label: '#',
      render: (_: any, index: number) => (page - 1) * 10 + (index + 1),
    },
    { label: 'City Name', render: (s: any) => s.name },
    { label: 'Arabic Name', render: (s: any) => s.arabicName },
    { label: 'City Code', render: (s: any) => s.cityCode || s.code },
    { label: 'Created At', render: (s: any) => formatDate(s.date) },
    { label: 'Alias English', render: (s: any) => s.aliasEnglish?.join(', ') },
    { label: 'Alias Arabic', render: (s: any) => s.aliasArabic?.join(', ') },
    {
      label: 'Action',
      render: (s: any) => (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => {
              setIsEdit(true);
              setSelectedItem(s);
              Object.keys(s).forEach((k) => setValue(k, s[k]));
              setIsModalOpen(true);
            }}
            className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-gray-200 transition"
          >
            <IconEdit size={18} />
          </button>
          <button
            onClick={() =>
              setTab({
                name: 'district',
                params: s.id,
                data: tab.data,
                cityName: s.name,
                countryId: tab.params,
              })
            }
            className="px-3 py-1.5 border rounded-full bg-[#F7F6F2] hover:bg-gray-200 transition flex items-center gap-2 text-sm"
          >
            <IconEye size={18} /> District
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6 mt-2">
        <div className="bg-white rounded-2xl border border-[#d2d2d2] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-base font-semibold">Cities in {tab.data}</h2>
            <div className="flex items-center gap-3">
              <NormalSearch
                value={searchFor}
                onChange={(e: any) => {
                  setSearchFor(e.target.value);
                  setPage(1);
                }}
              />
              {/* <button
                onClick={() => {
                  setIsEdit(false);
                  setIsModalOpen(true);
                  setValue('countryId', tab.params);
                }}
                className="px-4 py-2 bg-[#FBD037] hover:bg-yellow-500 text-black font-semibold rounded-full transition-all text-sm flex items-center justify-center"
              >
                Add City
              </button> */}
            </div>
          </div>

          {isLoading ? (
            <TableSkeleton
              columns={[
                '#',
                'City Name',
                'Arabic Name',
                'City Code',
                'Created At',
                'Alias English',
                'Alias Arabic',
                'Action',
              ]}
              rows={10}
            />
          ) : cityData?.data?.cities?.length > 0 ? (
            <ListView columns={columns} data={cityData.data.cities} />
          ) : (
            <div className="p-8 text-center text-gray-500">No Data Found</div>
          )}
        </div>

        {cityData?.data?.totalPages > 1 && (
          <Pagination
            totalPages={cityData.data.totalPages}
            fetchData={setPage}
            currentPage={page}
          />
        )}
      </div>

      <NewCommonModal
        title={isEdit ? 'Update City' : 'Add New City'}
        open={isModalOpen}
        onClose={closeAndReset}
        actionButtonText={isEdit ? 'Update' : 'Save'}
        actionButtonOnClick={handleSubmit(handleFormSubmit)}
        closeButtonText="Cancel"
      >
        <div className="bg-white p-6 rounded-b-[30px]">
          <DynamicForm
            schema={citySchema}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            control={control}
            clearErrors={clearErrors}
            className="grid-cols-1 md:grid-cols-3"
          />
        </div>
      </NewCommonModal>
    </>
  );
}
